<template>
  <detail-card section-key="Underlyings">
    <b-row class="underlyings-asset-class-row">
      <b-col cols="12"><span class="font-weight-bold">{{ assetClassLabel }}:</span> {{ assetClassValue }}</b-col>
    </b-row>
    <b-row v-if="names.length > 0" class="underlyings-table-row">
      <b-col cols="12">
        <b-table striped hover :items="nameItems" :fields="nameFields" class="underlyings-table w-100"></b-table>
      </b-col>
    </b-row>
  </detail-card>
</template>

<script>
// "Sottostanti" (Underlyings), IC-only, `KpiKid` tab (DETAIL_PAGE_DESIGN.md: "Sottostanti" is IC's
// own reference-asset anagraphic list — a structurally different concept from IBIPs' "Opzioni di
// investimento"). Rendered as a bordered card using
// DetailCard.vue, the same shared shell FieldCatalogSection.vue/LiquiditySection.vue use, even though
// every OTHER KpiKid-tab typed section (RiskProfile/CostsProfile/PerformanceScenarios/
// PerformanceIndicators) is a borderless box.
//
// The names list renders as a one-column `Sottostante` table with an orange header —
// deliberately NOT the page's shared global `.results` class (see ProductDetails.vue's
// own <style> block): `.results th/td { width: 20% !important }` plus a 40%-width first-column
// override assume a multi-column Costs/Scenarios/Performance table; on a genuinely single-column
// table that would leave 60% of the row visually empty instead of spanning full width,
// so this gets its own small scoped `.underlyings-table` class instead of touching that
// shared global rule (which several other typed sections already depend on as-is).
//
// Field-catalog labels/enum values are reused rather than duplicated: `productDetails.fields.
// Underlyings.*` are new keys following detailFieldLabel's existing `productDetails.fields.
// <sectionKey>.<fieldKey>` convention (this section isn't Fields-shaped, but the same path keeps
// every label lookup in the page uniform); `UnderlyingAssetClass`'s 8 enum member labels reuse
// `search.options.UnderlyingAssetClass.*` verbatim — the same enum (Product.UnderlyingAssetClass),
// already used by the search filter UI — rather than keeping a second,
// `productDetails.options.*` copy of the exact same 8 labels. A wrong i18n path here would
// silently fall back to the raw .NET enum name, e.g. "SingleStock" instead of "Azione singola".
import DetailCard from '../DetailCard'
import { detailFieldLabel } from '../../../utils/detailFieldFormat'

export default {
  name: 'underlyings-section',
  components: {
    DetailCard,
  },
  props: {
    payload: { type: Object, required: true },
  },
  computed: {
    assetClassLabel() {
      return detailFieldLabel(this, 'Underlyings', 'underlyingAssetClass')
    },
    nameColumnLabel() {
      return detailFieldLabel(this, 'Underlyings', 'name')
    },
    assetClassValue() {
      const value = this.payload.underlyingAssetClass
      if (value == null) {
        return this.$t('common.notAvailable')
      }
      const key = `search.options.UnderlyingAssetClass.${value}`
      return this.$te(key) ? this.$t(key) : value
    },
    names() {
      return this.payload.names || []
    },
    nameItems() {
      return this.names.map((name) => ({ name }))
    },
    nameFields() {
      return [{ key: 'name', label: this.nameColumnLabel }]
    },
  },
}
</script>

<style lang="scss" scoped>
// User-requested (screenshot-confirmed) — the actual gap, on rereading the screenshots against the
// DOM, was NEVER between "Tipo sottostante: ..." and the table (the first two attempts at this fix
// targeted that one and correctly had zero visible effect, per the user's own report, since they
// weren't looking at that gap at all): it's the gap ABOVE "Tipo sottostante: ...", between the card's
// header divider and this row. DetailCard.vue's shared `/deep/ .row:nth-child(2) { margin-top: 2rem;
// }` targets this row specifically — it's the section's first content row (the header is child 1) —
// tuned for the OTHER field-catalog sections' stacked label/value rows, where a bigger gap after the
// header reads fine; on Underlyings' label+table layout it just looks like dead space before the
// table even starts. Overridden down to the same 0.5rem rhythm the shared `/deep/ .row` bottom-margin
// rule already uses elsewhere, `margin-bottom: 0` kept from the earlier (correct, if aimed at the
// wrong gap) attempt — no reason to reintroduce that one now that this row's real spacing is
// intentional rather than inherited.
.underlyings-asset-class-row {
  margin-top: 0.5rem !important;
  margin-bottom: 0 !important;
}

// Same story as the top gap, mirrored at the bottom: DetailCard.vue's shared `/deep/ .row:last-child
// { margin-bottom: 2rem; }` targets this row (it's the card's last row whenever the table renders)
// — sized for the OTHER field-catalog sections' own trailing whitespace, too much once the card ends
// right after a table instead of a label/value row.
.underlyings-table-row {
  margin-bottom: 0.5rem !important;
}

.underlyings-table {
  /deep/ th {
    padding: 5px 10px;
    background-color: var(--primary);
    color: white;
    width: 100%;
  }

  /deep/ td {
    padding: 5px 10px;
  }
}
</style>
